// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Slider } from '@advisor-app/components';
import { formatToMoney } from '@advisor-app/features/utils/format-to-money';
import { numberToFixed } from '@shared/ui-kit/utils';
import { selectWorkflowType } from '@advisor-app/store/slices/workflow';
import { useAppSelector } from '@advisor-app/store/store';
import { Heading } from '@shared/ui-kit/components';
import { useMemo } from 'react';
import { NumericFormat } from 'react-number-format';

interface RebalanceSliderCashProps {
  title?: string;
  placeholder: string;
  percentageSlider?: boolean;
  prefix?: string;
  suffix?: string;
  disabled?: boolean;
  value: number;
  totalInvestment?: number;
  rebalanceType?: string;
  onInputBlur?: () => void;
  onChangeCommitted?: () => void;
  onChangeInputValue: (value: number) => void;
  onChangeSlider: (value: number) => void;
}

export const RebalanceSliderCash = ({
  title = '',
  placeholder,
  percentageSlider,
  prefix,
  suffix,
  disabled,
  totalInvestment,
  rebalanceType,
  onChangeCommitted,
  onChangeInputValue,
  onChangeSlider,
  onInputBlur,
  value,
}: RebalanceSliderCashProps): JSX.Element => {
  const workflow = useAppSelector(selectWorkflowType);

  const minVal = useMemo(() => {
    /* istanbul ignore next */
    return workflow !== 'new' ? 0 : 5000;
  }, [workflow]);

  let maxVal = percentageSlider ? 100 : 10000000;

  if (rebalanceType === 'withdrawal' && totalInvestment)
    maxVal = numberToFixed(totalInvestment, 0);

  /* istanbul ignore next */
  if (value > maxVal) value = maxVal;

  return (
    <div>
      <Heading variant="h3" title={title} />
      <div className={disabled ? 'box_disabled mb-3' : 'my-[18px]'}>
        <NumericFormat
          value={disabled ? 0 : value}
          thousandSeparator
          min={0}
          maxLength={11}
          isAllowed={({ value }) => {
            return Number(value) <= maxVal;
          }}
          onValueChange={({ value }) => onChangeInputValue(Number(value))}
          onBlur={onInputBlur}
          prefix={prefix}
          suffix={suffix}
          placeholder={placeholder}
          className="input-common !money-display"
          id={placeholder}
          data-testid="rebalance-cash-input"
        />
      </div>

      <Slider
        valueLabelDisplay="off"
        onChange={(_, newValue) => {
          onChangeSlider(newValue as number);
        }}
        onChangeCommitted={onChangeCommitted}
        value={value}
        min={minVal}
        max={maxVal}
        step={percentageSlider ? 5 : 1000}
        valueLabelFormat={
          percentageSlider ? val => `${val} %` : val => formatToMoney(val)
        }
        data-testid="slider"
        disabled={disabled ? true : false}
      />
    </div>
  );
};
